import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { PublicUserTagGroup } from '~/javascripts/admin/actions/organization-applications';
import { getUserTagGroupDetail } from '~/javascripts/admin/actions/user-tag-group';
import MerchantApplicationForm, {
  MerchantApplicationFormData,
} from '../MerchantApplicationForm';

type RawUserTagGroupOption = {
  user_tag_group_id: string;
  field_option: 'required' | 'optional';
};

type EnrichedUserTagGroupOption = RawUserTagGroupOption & {
  user_tag_group: PublicUserTagGroup;
};

const parseUserTagGroupOptions = (
  param: string | null
): RawUserTagGroupOption[] | undefined => {
  if (!param) return undefined;
  try {
    const parsed = JSON.parse(param);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    // Invalid JSON, return undefined
  }
  return undefined;
};

const MerchantApplicationFormPreview = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userTagGroupOptionsParam = params.get('user_tag_group_options');

  const rawOptions = useMemo(
    () => parseUserTagGroupOptions(userTagGroupOptionsParam),
    [userTagGroupOptionsParam]
  );

  // null = fetching, undefined = no options requested, [] = empty list, [...] = loaded
  const [enrichedOptions, setEnrichedOptions] = useState<
    EnrichedUserTagGroupOption[] | null | undefined
  >(rawOptions === undefined ? undefined : rawOptions.length === 0 ? [] : null);

  useEffect(() => {
    if (!rawOptions || rawOptions.length === 0) {
      setEnrichedOptions(rawOptions === undefined ? undefined : []);
      return;
    }

    setEnrichedOptions(null);

    let cancelled = false;
    Promise.allSettled(
      rawOptions.map((opt) =>
        getUserTagGroupDetail(opt.user_tag_group_id).then((detail) => {
          // active な subgroup 集合で照合し、それに属さない item は未分類扱いにする
          //（getActiveUserTagGroupsWithItems と同じ正規化。orphan item の取りこぼしを防ぐ）。
          const activeSubgroups = (detail.subgroups ?? []).filter((sg) => sg.is_active);
          const activeSubgroupIds = new Set(activeSubgroups.map((sg) => sg.id));
          const items = detail.items.map((item) => ({
            id: item.id,
            name_ja: item.name_ja,
            name_en: item.name_en,
            name_zh_hans: item.name_zh_hans,
            name_zh_hant: item.name_zh_hant,
            name_ko: item.name_ko,
            is_active: item.is_active,
            user_tag_subgroup_id:
              item.user_tag_subgroup && activeSubgroupIds.has(item.user_tag_subgroup.id)
                ? item.user_tag_subgroup.id
                : null,
          }));
          return {
            user_tag_group_id: opt.user_tag_group_id,
            field_option: opt.field_option,
            user_tag_group: {
              id: detail.id,
              name_ja: detail.name_ja,
              name_en: detail.name_en,
              name_zh_hans: detail.name_zh_hans,
              name_zh_hant: detail.name_zh_hant,
              name_ko: detail.name_ko,
              max_selections: detail.max_selections,
              // PublicUserTagGroup の型パリティのため公開 API と同じネスト構造を再現する。
              // フォームは subgroups から見出し名・表示順のみ使い、選択肢はフラット items から
              // 再導出するため、この nested items 自体は消費されない。
              subgroups: activeSubgroups.map((sg) => ({
                id: sg.id,
                name_ja: sg.name_ja,
                name_en: sg.name_en,
                name_zh_hans: sg.name_zh_hans,
                name_zh_hant: sg.name_zh_hant,
                name_ko: sg.name_ko,
                items: items.filter((it) => it.user_tag_subgroup_id === sg.id),
              })),
              items,
            },
          };
        })
      )
    ).then((results) => {
      if (cancelled) return;
      const enriched: EnrichedUserTagGroupOption[] = [];
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          enriched.push(result.value);
        } else {
          console.error(
            'Failed to fetch user tag group:',
            rawOptions[i].user_tag_group_id,
            result.reason
          );
        }
      });
      setEnrichedOptions(enriched);
    });

    return () => {
      cancelled = true;
    };
  }, [rawOptions]);

  if (enrichedOptions === null) {
    return <h5>Loading...</h5>;
  }

  const data: MerchantApplicationFormData = {
    templateId: '', // Not used in preview mode
    title: params.get('title') || '',
    applicationTermUrl: params.get('application_term_url') || '',
    redirectUrl: params.get('redirect_url') || null,
    // プレビューではマネー名を解決しないため空配列（完了画面はプレビューでは到達しない）。
    privateMoneys: [],
    privateMoneyIds: params.get('private_money_ids')?.split(',').filter(Boolean) || [],
    capitalFieldOption:
      (params.get('capital_field_option') as MerchantApplicationFormData['capitalFieldOption']) ||
      'optional',
    numberOfEmployeesFieldOption:
      (params.get(
        'number_of_employees_field_option'
      ) as MerchantApplicationFormData['numberOfEmployeesFieldOption']) || 'optional',
    referralSourceFieldOption:
      (params.get(
        'referral_source_field_option'
      ) as MerchantApplicationFormData['referralSourceFieldOption']) || 'optional',
    description: params.get('description') || '',
    userTagGroupOptions: enrichedOptions,
  };

  return <MerchantApplicationForm mode="preview" data={data} />;
};

export default MerchantApplicationFormPreview;
